"use client"

import { useEffect, useState, useCallback } from "react"
import { listGallery } from "@/src/utils/galleryApi"
import type { GalleryListFilters, GalleryListSort, GalleryItem } from "@/src/types/gallery"

type PageState = {
  items: GalleryItem[]
  page: number
  pageSize: number
  total: number
  loading: boolean
  error?: string | null
}

const DEFAULT_PAGE_SIZE = 12

export function useGalleryPaged(
  initialFilters?: GalleryListFilters,
  initialSort: GalleryListSort = "created_desc",
  initialPageSize = DEFAULT_PAGE_SIZE,
  seed?: string,
  viewerUserId?: string,
) {
  const [filters, setFiltersState] = useState<GalleryListFilters | undefined>(initialFilters)
  const [sort, setSortState] = useState<GalleryListSort>(initialSort)
  const [state, setState] = useState<PageState>({
    items: [],
    page: 1,
    pageSize: initialPageSize,
    total: 0,
    loading: false,
    error: null,
  })

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      setState((s) => ({ ...s, loading: true, error: null }))
      const res = await listGallery({ page: state.page, pageSize: initialPageSize, sort, filters, seed, viewerUserId })
      if (cancelled) return
      if (!res.success) {
        setState((s) => ({ ...s, loading: false, error: res.error ?? "Failed to load gallery." }))
        return
      }
      setState((s) => ({
        ...s,
        items: res.items,
        page: res.page,
        pageSize: res.pageSize,
        total: res.total,
        loading: false,
        error: null,
      }))
    })()
    return () => {
      cancelled = true
    }
  }, [state.page, initialPageSize, sort, JSON.stringify(filters), seed, viewerUserId])

  const totalPages = Math.max(1, Math.ceil(state.total / state.pageSize))
  const hasPrev = state.page > 1
  const hasNext = state.page < totalPages

  const setPage = useCallback((newPage: number) => {
    setState((s) => ({ ...s, page: newPage }))
  }, [])

  const setFilters = useCallback((newFilters: GalleryListFilters | undefined) => {
    setFiltersState(newFilters)
  }, [])

  const setSort = useCallback((newSort: GalleryListSort) => {
    setSortState(newSort)
  }, [])

  const reset = useCallback(() => {
    setState((s) => ({ ...s, page: 1 }))
  }, [])

  return {
    ...state,
    totalPages,
    hasPrev,
    hasNext,
    setPage,
    setFilters,
    setSort,
    reset,
  }
}
